interface FortuneTimelineProps {
  startYear: number;
  years: number;
}

export default function FortuneTimeline({ startYear, years }: FortuneTimelineProps) {
  // 운세 강도 생성 (0-100)
  const generateFortune = (index: number) => {
    // 사인파 패턴으로 자연스러운 운세 흐름 생성
    const base = 50 + Math.sin(index * 0.5) * 30;
    const variation = Math.sin(index * 1.2) * 15;
    return Math.max(20, Math.min(95, base + variation));
  };

  const timelineData = Array.from({ length: years }, (_, i) => ({
    year: startYear + i,
    fortune: generateFortune(i),
    isPresent: i === 0,
  }));

  const getFortuneColor = (fortune: number) => {
    if (fortune >= 75) return { bg: 'from-green-500 to-emerald-600', text: 'text-green-300', glow: 'shadow-green-400/50', label: '대길' };
    if (fortune >= 60) return { bg: 'from-blue-500 to-blue-600', text: 'text-blue-300', glow: 'shadow-blue-400/50', label: '길' };
    if (fortune >= 40) return { bg: 'from-yellow-500 to-amber-600', text: 'text-yellow-300', glow: 'shadow-yellow-400/50', label: '평' };
    return { bg: 'from-orange-500 to-red-600', text: 'text-orange-300', glow: 'shadow-orange-400/50', label: '주의' };
  };

  return (
    <div className="relative">
      {/* 타임라인 컨테이너 */}
      <div className="overflow-x-auto pb-4 scrollbar-thin scrollbar-thumb-gold-400/30 scrollbar-track-white/5">
        <div className="flex gap-3 sm:gap-4 min-w-max px-2">
          {timelineData.map((data, index) => {
            const colorScheme = getFortuneColor(data.fortune);
            const height = `${data.fortune}%`;
            
            return (
              <div
                key={data.year}
                className="flex flex-col items-center gap-3 group"
                style={{ minWidth: years > 15 ? '60px' : '80px' }}
              >
                {/* 운세 바 */}
                <div className="relative w-full h-48 sm:h-56 bg-white/5 rounded-2xl overflow-hidden border border-white/10 group-hover:border-gold-400/50 transition-all">
                  {/* 배경 그리드 */}
                  <div className="absolute inset-0">
                    {[25, 50, 75].map((line) => (
                      <div
                        key={line}
                        className="absolute w-full border-t border-white/5"
                        style={{ bottom: `${line}%` }}
                      ></div>
                    ))}
                  </div>
                  
                  {/* 운세 바 */}
                  <div
                    className={`absolute bottom-0 w-full bg-gradient-to-t ${colorScheme.bg} transition-all duration-500 group-hover:scale-105 rounded-t-xl ${colorScheme.glow} shadow-lg`}
                    style={{ height }}
                  >
                    {/* 반짝이는 효과 */}
                    <div className="absolute inset-0 bg-gradient-to-t from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  </div>
                  
                  {/* 운세 수치 */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className={`text-lg sm:text-xl font-bold ${colorScheme.text} drop-shadow-lg opacity-0 group-hover:opacity-100 transition-opacity`}>
                      {Math.round(data.fortune)}
                    </span>
                  </div>
                  
                  {/* 현재 연도 표시 */}
                  {data.isPresent && (
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2">
                      <div className="bg-gradient-to-r from-gold-400 to-gold-500 text-indigo-950 text-xs font-bold px-3 py-1 rounded-full shadow-lg shadow-gold-400/50 animate-pulse">
                        현재
                      </div>
                    </div>
                  )}
                </div>

                {/* 연도 및 운세 레이블 */}
                <div className="text-center">
                  <p className={`text-sm sm:text-base font-bold mb-1 ${data.isPresent ? 'text-gold-400' : 'text-white'} group-hover:text-gold-400 transition-colors`}>
                    {data.year}
                  </p>
                  <div className={`text-xs px-2 py-1 rounded-full bg-gradient-to-r ${colorScheme.bg} text-white font-medium opacity-0 group-hover:opacity-100 transition-opacity`}>
                    {colorScheme.label}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 범례 */}
      <div className="flex flex-wrap justify-center gap-4 mt-6 pt-6 border-t border-white/10">
        {[
          { label: '대길', range: '75-100', color: 'from-green-500 to-emerald-600' },
          { label: '길', range: '60-74', color: 'from-blue-500 to-blue-600' },
          { label: '평', range: '40-59', color: 'from-yellow-500 to-amber-600' },
          { label: '주의', range: '20-39', color: 'from-orange-500 to-red-600' },
        ].map((item) => (
          <div key={item.label} className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${item.color} shadow-lg`}></div>
            <div>
              <p className="text-white text-sm font-semibold">{item.label}</p>
              <p className="text-white/50 text-xs">{item.range}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}